import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatUsd } from '../lib/format';
import { SupplementSummary } from './SupplementSummary';
import type { NegData } from './NegotiationPanel';

type Card = {
  insurance_first_cents?: number | null; insurance_estimate_cents?: number | null;
  supplement_resolved_at?: string | null; first_estimate_at?: string | null;
};

/**
 * Comparativo de PRIMERO en la pestaña Supplements (feedback 2026-07-30): primer estimado del
 * seguro vs el último, y cuánto subió gracias al supplement — bien visible, con color y un
 * latido suave (mismos montos que el chip del board de Supplements, `insurance_first_cents`/
 * `insurance_estimate_cents` de `GET /api/supplements/cards/:id`, así nunca se desincroniza).
 */
export function SupplementEstimateCompare({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [card, setCard] = useState<Card | null>(null);
  const [loaded, setLoaded] = useState(false);
  // El RESUMEN del análisis de Review Estimate (qué estamos pidiendo/disputando) — el MISMO
  // que se muestra en Supplementing (pedido 2026-08-18).
  const [neg, setNeg] = useState<NegData | null>(null);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    api.get<{ card: Card | null }>(`/api/supplements/cards/${claimId}`)
      .then((r) => { if (alive) setCard(r.card); })
      .catch(() => { if (alive) setCard(null); })
      .finally(() => { if (alive) setLoaded(true); });
    api.get<{ negotiation: NegData | null }>(`/api/claims/${claimId}/negotiation`)
      .then((r) => { if (alive) setNeg(r.negotiation); })
      .catch(() => { if (alive) setNeg(null); });
    return () => { alive = false; };
  }, [claimId]);

  if (!loaded) return null;
  const first = card?.insurance_first_cents ?? null;
  const last = card?.insurance_estimate_cents ?? null;
  if (first == null || last == null) {
    // Sin los dos estimados no hay tira comparativa, pero el resumen del análisis sí puede existir.
    return neg ? <div className="card"><SupplementSummary neg={neg} /></div> : null;
  }

  const diff = last - first;
  const good = diff >= 0;

  return (
    <>
    <div className="card sup-compare">
      <div className="sup-compare-stat">
        <div className="sup-compare-label">{t('supCmp.first')}</div>
        <div className="sup-compare-value">{formatUsd(first)}</div>
      </div>
      <div className="sup-compare-arrow">→</div>
      <div className="sup-compare-stat">
        <div className="sup-compare-label">{t('supCmp.last')}</div>
        <div className="sup-compare-value">{formatUsd(last)}</div>
      </div>
      {/* RESOLVED IN — N days (pedido 2026-08-13): del PRIMER estimado a la fecha de Resolved. */}
      {card?.supplement_resolved_at && card?.first_estimate_at && (() => {
        const dias = Math.max(0, Math.round((new Date(card.supplement_resolved_at!).getTime() - new Date(card.first_estimate_at!).getTime()) / 86400000));
        return (
          <div className="sup-compare-stat">
            <div className="sup-compare-label">{t('supCmp.resolvedIn')}</div>
            <div className="sup-compare-value" style={{ color: 'var(--green)' }}>{t('supCmp.resolvedInDays', { n: String(dias) })}</div>
          </div>
        );
      })()}
      {diff !== 0 && (
        <div className={`sup-compare-diff gap-pulse ${good ? 'up' : 'down'}`}>
          <div className="sup-compare-label">{good ? t('supCmp.gained') : t('supCmp.lost')}</div>
          <div className="sup-compare-value">{good ? '▲' : '▼'} {formatUsd(Math.abs(diff))}</div>
        </div>
      )}
    </div>
    {/* Debajo de la tira: QUÉ estamos pidiendo (el resumen del analyze de Review Estimate,
        idéntico al de Supplementing). */}
    {neg && <div className="card"><SupplementSummary neg={neg} /></div>}
    </>
  );
}
